import React from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import { MsalProvider } from '@azure/msal-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { msalInstance } from '@/lib/auth'
import { PersonaProvider } from '@/lib/personas'
import { ToastProvider } from '@/components/Toast'
import App from './App'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
})

// Set data-theme synchronously before React renders, so CSS vars resolve
// correctly on first paint. Without this, child useEffects (notably the orb,
// which reads getComputedStyle on init) fire before PersonaProvider's effect
// gets a chance to set the attribute, and we lock in the wrong palette.
;(() => {
  if (typeof window === 'undefined') return
  const v = window.localStorage.getItem('jarvis.persona')
  const persona = v === 'ultron' || v === 'ares' ? v : 'jarvis'
  document.documentElement.setAttribute('data-theme', persona)
})()

async function bootstrap() {
  try {
    await msalInstance.initialize()
    // Process any redirect response BEFORE React mounts so the very first
    // render of <ProtectedRoute> already sees an authenticated state. Without
    // this we relied on @azure/msal-react to call handleRedirectPromise after
    // mount, which raced ProtectedRoute's auth check and bounced the user to
    // /login on the post-login page load.
    const response = await msalInstance.handleRedirectPromise()
    if (response?.account) {
      msalInstance.setActiveAccount(response.account)
    } else {
      const existing = msalInstance.getAllAccounts()
      if (existing.length > 0) msalInstance.setActiveAccount(existing[0])
    }
  } catch (e) {
    console.error('[auth] MSAL bootstrap failed', e)
  }

  const rootEl = document.getElementById('root')!
  const tree = (
    <React.StrictMode>
      <HelmetProvider>
        <PersonaProvider>
          <MsalProvider instance={msalInstance}>
            <QueryClientProvider client={queryClient}>
              <ToastProvider>
                <App />
              </ToastProvider>
            </QueryClientProvider>
          </MsalProvider>
        </PersonaProvider>
      </HelmetProvider>
    </React.StrictMode>
  )

  // react-snap pre-fills #root at build time → hydrate; otherwise fresh render.
  if (rootEl.hasChildNodes()) {
    hydrateRoot(rootEl, tree)
  } else {
    createRoot(rootEl).render(tree)
  }
}

bootstrap()
